import ControllerBase from '../../../../global/js/ControllerBase';
import { MHeroSelectorAppDataTypes, POCTypes, SelectorPair } from './m-hero.contact-selector.types';

const PREFIX = "m-hero-contact-selector"
const M_HERO_SELECTOR_DATA_APP = PREFIX + "-app-data"
const CATEGORY_SELECTOR = PREFIX + "__category-selector"
const DEPARTMENT_SELECTOR = PREFIX + "__department-selector"
const POC_IMAGE_SELECTOR = PREFIX + '__poc-image'
const POC_MAINLINE_SELECTOR = PREFIX + '__poc-mainline'
const POC_CONTAINER_SELECTOR = PREFIX + '__poc-data-container'
const POC_DATA_ELEMENT_CLASS = PREFIX + '__poc-description'

type ComponentRefs = {
	categorySelector: string;
	dynamicSelector: string;
	pocContainer: string;
	pocImageSelector: string;
	pocMainLineSelector: string;
};

type ComponentState = MHeroSelectorAppDataTypes & { subcategorySelector: SelectorPair[], personOfContact: POCTypes | null }

export default class ControllerHeroContactSelector extends ControllerBase<ComponentRefs> {

	private state: ComponentState = {
		categorySelector: [],
		subcategorySelector: [],
		contactPersons: [],
		contactPlaceholder: {
			image: '',
			copy: {
				text: ''
			}
		},
		personOfContact: null
	}

	private static readonly componentRefs: ComponentRefs = {
		categorySelector: CATEGORY_SELECTOR,
		dynamicSelector: DEPARTMENT_SELECTOR,
		pocImageSelector: POC_IMAGE_SELECTOR,
		pocMainLineSelector: POC_MAINLINE_SELECTOR,
		pocContainer: POC_CONTAINER_SELECTOR
	};

	constructor(element: HTMLElement) {
		super(element, [], Object.values(ControllerHeroContactSelector.componentRefs));
		this.initApp();
	}

	private initApp(): void {
		this.setInitialState()
	}

	private setState(payload: Partial<ComponentState>): void {
		this.state = { ...this.state, ...payload }
	}

	private setInitialState = () => {
		const data = document.getElementById(M_HERO_SELECTOR_DATA_APP) as HTMLScriptElement;
		const initdata = JSON.parse(data.innerText) as MHeroSelectorAppDataTypes
		this.setState(initdata)
		this.printOptionsOnSelect(initdata.categorySelector, this.$references[CATEGORY_SELECTOR])
	}

	private cleanAllChildNodesFromElement(element: HTMLElement) {
		while (element.firstChild && element.lastChild) {
			element.removeChild(element.lastChild);
		}
	}

	private printOptionsOnSelect(data: MHeroSelectorAppDataTypes['categorySelector'], ref: HTMLSelectElement) {

		const isCategorySelector = ref.dataset.ref === CATEGORY_SELECTOR
		const isDepartmentSelector = ref.dataset.ref === DEPARTMENT_SELECTOR

		data.forEach(({ value, label, subcategoryOptions }) => {
			const option = document.createElement('option');

			if (value) {
				option.value = value;
			} else {
				option.disabled = true
				option.selected = true
			}

			option.innerHTML = label;

			if (isCategorySelector) option['data-subcategoryOptions'] = subcategoryOptions

			ref.appendChild(option)
		})

		if (isCategorySelector) {
			ref.addEventListener("change", this.listenCategorySelectorActivity.bind(this), false)
			this.printOptionsOnSelect([ref.options[1]['data-subcategoryOptions'][0]],this.$references[DEPARTMENT_SELECTOR])
		} 

		if (isDepartmentSelector) ref.addEventListener("change", this.updatePOCBasedOnDepartment.bind(this), false)

		if (ref.options[0].value === ref.options[ref.options.selectedIndex].value) {
			ref.classList.add('select-placeholder-option')
		}
	}

	private listenCategorySelectorActivity(event: Event) {
		const target = event.target as HTMLSelectElement
		this.resetPersonOfContactOnDom()

		if (target) {

			const departmentSelector = this.$references[DEPARTMENT_SELECTOR]
			const currentOptionsSubCategory = target[target.options.selectedIndex]['data-subcategoryOptions'] as SelectorPair[]

			this.cleanOptionsOnSelect(departmentSelector)

			this.setState({ subcategorySelector: currentOptionsSubCategory })

			this.printOptionsOnSelect(this.state.subcategorySelector, departmentSelector)
			departmentSelector.disabled = false

			target.classList.remove('select-placeholder-option')
		}
	}

	private cleanOptionsOnSelect(select: HTMLSelectElement) {
		if (select) {
			const selectOptions = select.options
			while (selectOptions.length > 0) {
				select.remove(0);
			}
		}
	}

	private updatePOCBasedOnDepartment(event: Event) {
		const target = event.target as HTMLSelectElement
		if (target) {
			const POCId = target.options[target.options.selectedIndex].value
			const personOfContact = this.state.contactPersons.find(({ id }) => id === POCId)
			this.setState({ personOfContact })
			this.updatePersonOfContactOnDom()

			target.classList.remove('select-placeholder-option')
		}
	}

	private updatePersonOfContactOnDom() {
		if (this.state.personOfContact) {
			this.$references[POC_IMAGE_SELECTOR].src = this.state.personOfContact.image

			this.cleanAllChildNodesFromElement(this.$references[POC_CONTAINER_SELECTOR])

			const mainlineElement = this.$references[POC_MAINLINE_SELECTOR].cloneNode([false])
			mainlineElement.innerText = `${this.state.personOfContact.firstname} ${this.state.personOfContact.lastname}`
			mainlineElement.dataset.ref = POC_CONTAINER_SELECTOR

			const positionElement = document.createElement('span')
			positionElement.innerText = this.state.personOfContact.position
			positionElement.classList.add('a-copy', POC_DATA_ELEMENT_CLASS, PREFIX + '__poc-position')

			const phoneNumber = document.createElement('a')
			phoneNumber.href = `tel:${this.state.personOfContact.email}`
			phoneNumber.innerText = this.state.personOfContact.phone
			phoneNumber.classList.add('a-copy', POC_DATA_ELEMENT_CLASS, PREFIX + '__poc-phoneNumber')

			const email = document.createElement('a')
			email.href = `mailto:${this.state.personOfContact.email}`
			email.innerText = this.state.personOfContact.email
			email.classList.add('a-copy', POC_DATA_ELEMENT_CLASS, PREFIX + '__poc-email')

			const elementsToPrint = [mainlineElement, positionElement, phoneNumber, email]

			elementsToPrint.forEach(element => {
				this.$references[POC_CONTAINER_SELECTOR].appendChild(element)
			});

		}
	}

	private resetPersonOfContactOnDom() {
		const mainLineElement = this.$references[POC_MAINLINE_SELECTOR].cloneNode([false])
		mainLineElement.innerText = this.state.contactPlaceholder.copy.text

		this.setState({ personOfContact: null })
		this.cleanAllChildNodesFromElement(this.$references[POC_CONTAINER_SELECTOR])

		this.$references[POC_IMAGE_SELECTOR].src = this.state.contactPlaceholder.image
		this.$references[POC_CONTAINER_SELECTOR].appendChild(mainLineElement)
	}
}